import React, { useState, useMemo } from 'react';
import { useAtomValue } from 'jotai';
import { Button, Space, Tooltip } from 'antd';
import update from 'immutability-helper';
import { get } from 'lodash';
import { EditOutlined } from '@ant-design/icons';
import { useWidget } from '../../state/dashboard';
import useFetchMetrics from './useFetchMetrics';
import useDiagDashboard from '../../hooks/useDiagDashboard';
import Drilldown from '../../control/Drilldown';
import RemoveWidget from '../../control/RemoveWidget';
import { Retry } from '@src/components/common';
import Edit from './Edit';
import MetricTrend from './MetricTrend';
import useQueryParams from '../../hooks/useQueryParams';
import { TrendWrapper, StyledCardWrapper } from './styled';
import WidgetCardWrapper from '../../common/WidgetCardWrapper';
import { unitAtom } from '@src/components/metrics-panel-hz/atoms';
import { getPercentageMetricByKey } from '@src/utils/metrics/metricHelpers';
import { getFilterFromRules, getRulesFromFilter } from '@src/utils';

function EventMetricGroup({ wid }) {
  const widgetHandler = useWidget({ wid });
  const { filter } = useQueryParams();
  const { metrics = [], groupName, eventName } = widgetHandler.state;
  const unit = useAtomValue(unitAtom);
  const [editMode, setEditMode] = useState(false);
  const showEdit = editMode || metrics.length === 0;

  const composedFilter = useMemo(() => {
    const eventRule = {
      display: eventName,
      field: 'event_name',
      key: null,
      op: 'eq',
      originalField: 'event_name',
      value: eventName,
    };

    const combinedFilter = update(filter, {
      $push: [[eventRule]],
    });

    const newRules = getRulesFromFilter(combinedFilter, true);
    return getFilterFromRules(newRules, true);
  }, [filter, eventName]);

  const { isFetching, isError, data, refetch, queued } = useFetchMetrics({
    wid,
    metrics,
    eventName,
    filter: composedFilter,
  });

  const { createDashboard } = useDiagDashboard({
    metrics,
    title: `${groupName} : ${eventName}`,
    filter: composedFilter,
  });

  const handleEditClick = () => {
    setEditMode((value) => !value);
  };

  const handleCancel = () => {
    setEditMode(false);
  };

  const handleSave = (newState) => {
    widgetHandler.update(newState);
    handleCancel();
  };

  const EventHeader = () => {
    return (
      <div>
        <span>Event: </span>
        <span>{eventName}</span>
      </div>
    );
  };

  return (
    <WidgetCardWrapper
      wid={wid}
      title={<EventHeader />}
      style={{ gridRow: 'span 14' }}
      extra={
        <Space>
          <Tooltip title="Edit">
            <Button icon={<EditOutlined />} onClick={handleEditClick} type="text" />
          </Tooltip>
          <Drilldown createDashboard={createDashboard} disabled={showEdit} />
          <RemoveWidget onClick={widgetHandler.remove} />
        </Space>
      }
    >
      {showEdit ? (
        <Edit
          wid={wid}
          selectedMetrics={metrics}
          onSave={handleSave}
          onCancel={handleCancel}
          selectedEventName={eventName}
        />
      ) : (
        <Retry
          isLoading={isFetching}
          showError={isError}
          fetchData={refetch}
          progress={true}
          queued={queued}
        >
          <StyledCardWrapper>
            <TrendWrapper>
              {metrics.map((metricKey, idx) => {
                let span = 1;
                if (metrics.length < 3) {
                  span = 2;
                } else if (metrics.length === 3) {
                  span = idx === 2 ? 2 : 1;
                }

                const percentageMetric = getPercentageMetricByKey({
                  metricKey,
                  defaultValue: metricKey,
                });
                const chartMetric = unit === '%' ? percentageMetric : metricKey;

                return (
                  <MetricTrend
                    style={{ gridColumn: `span ${span}` }}
                    wid={wid}
                    key={chartMetric}
                    metricKey={chartMetric}
                    data={get(data, ['data', chartMetric], [])}
                    total={get(data, ['totals', chartMetric], null)}
                  />
                );
              })}
            </TrendWrapper>
          </StyledCardWrapper>
        </Retry>
      )}
    </WidgetCardWrapper>
  );
}

export default EventMetricGroup;
